import {
  ApprovalStatusEnum,
  IContract,
  IContractWithQuotes,
  IOfferingRes,
  IQuoteItemReqSchema,
  IQuoteItemRespSchema,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IQuotePrice,
  IQuoteRequestSchema,
  IQuoteRespSchema,
  IRateResBaseSchema,
  IRateResSchema,
  OfferingTypesEnum,
  PriceModelEnum,
  ProductTypeEnum,
  ProrationPolicyEnum,
  QuoteItemAmendmentStatusEnum,
  QuoteItemReqSchema,
  QuoteOfferingGroup,
  QuoteOfferingGroupRequest,
  QuoteOfferingGroupTypeSchema,
  QuoteOfferingReqSchema,
  QuoteOfferingWithChildren,
  QuoteRequestSchema,
  QuoteStatusEnum,
  QuoteTypeEnum,
  QuoteTypeEnumDisplay,
  RateBillingFrequencyEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import {
  formatCurrency,
  formatInteger,
  getCurrencyDecimals,
  getNextOccurrenceSameDayOfMonth,
  getRelevantDayInMonth,
  sortByProductPosition,
  toDateOnly,
} from '@monetize/utils/core';
import { addDays } from 'date-fns/addDays';
import { addYears } from 'date-fns/addYears';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { format } from 'date-fns/format';
import { getDate } from 'date-fns/getDate';
import { isBefore } from 'date-fns/isBefore';
import { isSameMonth } from 'date-fns/isSameMonth';
import { parseISO } from 'date-fns/parseISO';
import groupBy from 'lodash/groupBy';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import orderBy from 'lodash/orderBy';
import pick from 'lodash/pick';
import uniq from 'lodash/uniq';
import { MdTimer } from 'react-icons/md';
import { NavigateFunction } from 'react-router-dom';
import {
  MAvatar,
  MFlex,
  MIcon,
  MIDCopyBox,
  MText,
  MTooltip,
} from '../components/Monetize';
import { DateDisplay } from '../components/Monetize/DateDisplay';
import QuoteStatus from '../components/Quotes/QuoteStatus';
import { ROUTES } from '../constants';
import { RATE_BILLING_FREQUENCY_SHORTCODE_DISPLAY } from '../constants/offerings';
import {
  ONE_TIME_PRODUCT_TYPES,
  ONETIME_TOKEN_PRODUCT_TYPES,
  VARIABLE_AMOUNT_PRODUCT_TYPES,
} from '../constants/products';
import {
  QUOTE_READONLY_STATUSES,
  QuoteStatusTagStyle,
} from '../constants/quotes';
import { logger } from '../services/logger';
import { navigateOrOpenNewWindow } from './misc';

/**
 * Sort QuoteItems for each offering on a quote
 * This mutates provided data
 */
export const sortQuoteItems = (quote: IQuoteRespSchema): IQuoteRespSchema => {
  try {
    quote?.quoteOfferings?.forEach((offering) => {
      if (Array.isArray(offering.items)) {
        offering.items = sortByProductPosition(offering.items);
      }
    });
  } catch (ex) {
    logger.warn('Failed to sort quote items', ex);
  }
  return quote;
};

export const editQuote = (
  navigate: NavigateFunction,
  quoteId: string,
  openInNewWindow = false,
) => {
  navigateOrOpenNewWindow(
    navigate,
    ROUTES.getQuoteEditRoute(quoteId),
    openInNewWindow,
  );
};

export const reviewQuote = (
  navigate: NavigateFunction,
  quoteId: string,
  openInNewWindow = false,
) => {
  navigateOrOpenNewWindow(
    navigate,
    ROUTES.getQuoteReviewRoute(quoteId),
    openInNewWindow,
  );
};

export const openQuote = (
  navigate: NavigateFunction,
  quoteId: string,
  status?: QuoteStatusEnum,
  openInNewWindow = false,
) => {
  if (!status) {
    navigateOrOpenNewWindow(
      navigate,
      ROUTES.getQuoteBaseRoute(quoteId),
      openInNewWindow,
    );
  } else if (status === QuoteStatusEnum.DRAFT) {
    editQuote(navigate, quoteId, openInNewWindow);
  } else {
    reviewQuote(navigate, quoteId, openInNewWindow);
  }
};

export const getQuoteLink = ({
  quoteId,
  status,
}: {
  quoteId: string;
  status?: QuoteStatusEnum;
}) => {
  if (!status) {
    return ROUTES.getQuoteBaseRoute(quoteId);
  }

  if (status === QuoteStatusEnum.DRAFT) {
    return ROUTES.getQuoteEditRoute(quoteId);
  }

  return ROUTES.getQuoteReviewRoute(quoteId);
};

/**
 * Whether the Cancel action should be offered for a quote.
 *
 * The quote page allows cancelation from any editable status, while the quote
 * list only offers it for drafts.
 */
export const canCancelQuote = ({
  status,
  canUpdateSales,
  isQuotesList = false,
}: {
  status: QuoteStatusEnum;
  canUpdateSales: boolean;
  isQuotesList?: boolean;
}): boolean => {
  if (!canUpdateSales || QUOTE_READONLY_STATUSES.has(status)) {
    return false;
  }
  return !isQuotesList || status === QuoteStatusEnum.DRAFT;
};

export const loadTimerIcon = (rowData: IQuoteRespSchema) => {
  const { status, approvalSubmittedAt, expirationDate } = rowData;
  if (
    status === QuoteStatusEnum.REVIEW &&
    approvalSubmittedAt &&
    isBefore(parseISO(approvalSubmittedAt), addDays(new Date(), -4))
  ) {
    return {
      icon: <MIcon as={MdTimer} w={4} h={5} ml={2} color="tPurple.safety" />,
      tooltip: 'Quote in an approval process for a long time',
    };
  }
  if (
    status === QuoteStatusEnum.SENT &&
    expirationDate &&
    isBefore(parseISO(expirationDate), addDays(new Date(), 10))
  ) {
    return {
      icon: <MIcon as={MdTimer} w={4} h={5} ml={2} color="tOrange.base" />,
      tooltip: 'Quote will expire soon',
    };
  }

  return {
    icon: '',
    tooltip: '',
  };
};

export const quoteOwnerBodyTemplate = (rowData: IQuoteRespSchema) => (
  <MFlex justify="center" align="center">
    <MTooltip
      shouldWrapChildren
      label={rowData.ownerName}
      placement="bottom-start"
    >
      <MAvatar name={rowData.ownerName} mr="0" />
    </MTooltip>
  </MFlex>
);

export const quoteIdBodyTemplate = (rowData: IQuoteRespSchema) => (
  <MTooltip label={rowData.id} placement="bottom-end">
    <MIDCopyBox copyValue={rowData.id} displayIcon={false} />
  </MTooltip>
);

export const timerTemplate = (rowData: IQuoteRespSchema) => {
  const timer = loadTimerIcon(rowData);
  return (
    <MTooltip label={timer.tooltip} placement="bottom-end">
      <MText>{timer.icon}</MText>
    </MTooltip>
  );
};

export const companyBodyTemplate = (rowData: IQuoteRespSchema) => (
  <MTooltip label="View Quote" placement="bottom-end">
    <MText lineClamp={1} title={rowData.accountName || ''}>
      {rowData.accountName ?? '-'}
    </MText>
  </MTooltip>
);

export const lastUpdateBodyTemplate = (rowData: IQuoteRespSchema) =>
  rowData.modifyDate && (
    <DateDisplay
      date={rowData.modifyDate}
      timezoneConfigKey="userTimezone"
      textProps={{ lineClamp: 1 }}
    />
  );

export const totalBodyTemplate = (rowData: IQuoteRespSchema) => (
  <MTooltip label={rowData.currency} placement="bottom-end">
    <MText lineClamp={1}>
      {formatCurrency(rowData.amount, { currency: rowData.currency })}
    </MText>
  </MTooltip>
);

export const statusBodyTemplate = (rowData: IQuoteRespSchema) => {
  if (rowData.status) {
    return <QuoteStatus status={rowData.status} />;
  }
  return null;
};
export const quoteStatusStyle = (rowDate: IQuoteRespSchema) => {
  const { status } = rowDate;

  const style = status
    ? QuoteStatusTagStyle[status as QuoteStatusEnum]
    : {
        color: 'tGray.darkGrayPurple',
        bgColor: 'transparent',
      };
  return style;
};
export const typeBodyTemplate = (rowData: IQuoteRespSchema) =>
  rowData.type && (
    <MText lineClamp={1}>{QuoteTypeEnumDisplay[rowData.type]}</MText>
  );

export const getIsCustomPriceUsed = (quote: IQuoteRespSchema): boolean =>
  quote.quoteOfferings
    ?.flatMap(({ items }) => items)
    .some(({ customPriceId }) => customPriceId);

export const getCustomRateNames = (rates: IRateResSchema[]) =>
  rates.reduce<{ rateName: string; offeringName: string }[]>((res, rate) => {
    if (rate.parentRateId) {
      res.push({ rateName: rate.name, offeringName: rate.offering.name });
    }
    return res;
  }, []);

export const hasVariableAmountProduct = (
  quote?: IQuoteRespSchema | null,
): boolean => {
  if (!quote?.quoteOfferings) {
    return false;
  }
  return quote.quoteOfferings.some(({ items }) =>
    items.some(
      ({ productType, amendmentStatus }) =>
        VARIABLE_AMOUNT_PRODUCT_TYPES.has(productType) &&
        amendmentStatus !== QuoteItemAmendmentStatusEnum.REMOVED,
    ),
  );
};

/**
 * Converts quote offerings into a list of root parent offerings and their children,
 * based on parentQuoteOfferingId.
 *
 * All children will be sorted based on their startDate.
 *
 * @param quoteOfferings Pre-sorted quote offerings
 * @returns
 */
export const getQuoteOfferingsWithChildren = (
  quoteOfferings: IQuoteOfferingRespSchema[],
) => {
  const quoteOfferingsWithChildren = Object.values(
    (quoteOfferings || []).reduce(
      (output: Record<string, QuoteOfferingWithChildren>, quoteOffering) => {
        if (!quoteOffering.parentQuoteOfferingId) {
          output[quoteOffering.id] = output[quoteOffering.id] || {
            quoteOffering,
            children: [],
          };
          output[quoteOffering.id].quoteOffering = quoteOffering;
        } else {
          output[quoteOffering.parentQuoteOfferingId] = output[
            quoteOffering.parentQuoteOfferingId
          ] || {
            quoteOffering: null,
            children: [],
          };
          output[quoteOffering.parentQuoteOfferingId].children.push(
            quoteOffering,
          );
        }
        return output;
      },
      {},
    ),
  );

  quoteOfferingsWithChildren
    .filter(({ quoteOffering }) => !!quoteOffering)
    .forEach((quoteOffering) => {
      quoteOffering.children = orderBy(quoteOffering.children, ['startDate']);
    });

  return quoteOfferingsWithChildren;
};

/**
 * Doing reverse what getQuoteOfferingsWithChildren does
 * Get quote offerings from parent offerings
 *
 * @param quoteOfferingsWithChildren QuoteOfferingWithChildren[]
 * @returns QuoteOffering[]
 */
export const getQuoteOfferingsFromParentOfferings = (
  quoteOfferingsWithChildren: QuoteOfferingWithChildren[],
) => {
  const quoteOfferings: IQuoteOfferingRespSchema[] = [];

  quoteOfferingsWithChildren.forEach((quoteOfferingWithChildren) => {
    const { children, ...quoteOffering } = quoteOfferingWithChildren;
    quoteOfferings.push(quoteOffering.quoteOffering);

    children.forEach((child) => {
      quoteOfferings.push(child);
    });
  });

  return quoteOfferings;
};

/**
 * Check if target is included in same quote offering group with source
 * @param sourceQuoteOffering Source quote offering
 * @param targetQuoteOffering Target one to check against source
 * @returns If same quote offering group, true, else false
 */
export const isSameQuoteOfferingGroup = (
  sourceQuoteOffering: IQuoteOfferingRespSchema,
  targetQuoteOffering: IQuoteOfferingRespSchema,
) => {
  if (sourceQuoteOffering.parentQuoteOfferingId) {
    return (
      targetQuoteOffering.parentQuoteOfferingId ===
        sourceQuoteOffering.parentQuoteOfferingId ||
      targetQuoteOffering.id === sourceQuoteOffering.parentQuoteOfferingId
    );
  } else {
    return (
      targetQuoteOffering.id === sourceQuoteOffering.id ||
      targetQuoteOffering.parentQuoteOfferingId === sourceQuoteOffering.id
    );
  }
};

/**
 * Return updated quote offerings. From new quote offerings, update only quote offerings within same quote offering group of actionQuoteOffering
 * @param oldQuoteOfferings existing quote offerings
 * @param newQuoteOfferings new quote offerings
 * @param actionQuoteOffering  quote offering that action was performed
 * @returns
 */
export const getUpdatedQuoteOfferingGroup = (
  oldQuoteOfferings: IQuoteOfferingRespSchema[],
  newQuoteOfferings: IQuoteOfferingRespSchema[],
  actionQuoteOffering: IQuoteOfferingRespSchema,
) => {
  return newQuoteOfferings.map((item) => {
    if (isSameQuoteOfferingGroup(actionQuoteOffering, item)) {
      return item;
    } else {
      return (
        oldQuoteOfferings.find((currentItem) => currentItem.id === item.id) ||
        item
      );
    }
  });
};
/**
 * A run of offerings rendered together on the quote page.
 * group === null represents a run of ungrouped offerings.
 */
export type QuoteOfferingPartition = {
  group: QuoteOfferingGroup | null;
  offerings: QuoteOfferingWithChildren[];
};

/**
 * Check if a quote offering belongs to a quote offering group, tolerating a
 * stale quoteOfferingIds list by also consulting the offering's groupId.
 * Suitable for resolving the group behind a per-offering edit form; NOT for
 * rendering membership — a stale groupId would keep an offering visually in
 * a group it was just moved out of. Use findQuoteOfferingGroupForOffering
 * for rendering.
 */
export const isQuoteOfferingInGroup = (
  quoteOffering: IQuoteOfferingRespSchema,
  group: QuoteOfferingGroup,
) =>
  group.id === quoteOffering.groupId ||
  group.quoteOfferingIds.includes(quoteOffering.id);

/**
 * Restrict a group membership list to ids the backend accepts: root (top
 * level) quote offerings that still exist on the quote. The API rejects
 * child/ramp segment ids ("only root quote offerings are allowed") —
 * segments follow their parent implicitly — and stale ids of deleted
 * offerings would poison every subsequent PUT of the group.
 */
export const sanitizeQuoteOfferingGroupMemberIds = (
  quoteOfferingIds: string[],
  quoteOfferings: Maybe<IQuoteOfferingRespSchema[]>,
): string[] => {
  const rootQuoteOfferingIds = new Set(
    (quoteOfferings || [])
      .filter(({ parentQuoteOfferingId }) => !parentQuoteOfferingId)
      .map(({ id }) => id),
  );
  return uniq(quoteOfferingIds).filter((quoteOfferingId) =>
    rootQuoteOfferingIds.has(quoteOfferingId),
  );
};

/**
 * Payload for a group PUT that only changes membership: the group's current
 * accounts/contacts/custom fields carried over verbatim, with a recomputed
 * quoteOfferingIds list. There are no add/remove endpoints — the PUT declares
 * the group's complete membership, so it doubles as move and remove.
 *
 * Stored members are sanitized before the arithmetic: the whole list is sent,
 * so one stale id (an offering since deleted from the quote) would fail the
 * request. Ids in `addQuoteOfferingIds` are trusted as given — they can belong
 * to offerings created moments ago that the cached quote hasn't picked up yet.
 */
export const buildQuoteOfferingGroupMembershipRequest = ({
  group,
  quoteOfferings,
  addQuoteOfferingIds = [],
  removeQuoteOfferingIds = [],
}: {
  group: QuoteOfferingGroup;
  quoteOfferings: Maybe<IQuoteOfferingRespSchema[]>;
  addQuoteOfferingIds?: string[];
  removeQuoteOfferingIds?: string[];
}): QuoteOfferingGroupRequest => ({
  groupType: group.groupType || QuoteOfferingGroupTypeSchema.ACCOUNT_HIERARCHY,
  billingAccountId: group.billingAccount?.id ?? '',
  shippingAccountId: group.shippingAccount?.id ?? '',
  billingContactId: group.billingContactId ?? '',
  shippingContactId: group.shippingContactId ?? '',
  customFields: group.customFields ?? undefined,
  quoteOfferingIds: uniq([
    ...sanitizeQuoteOfferingGroupMemberIds(
      group.quoteOfferingIds,
      quoteOfferings,
    ).filter((memberId) => !removeQuoteOfferingIds.includes(memberId)),
    ...addQuoteOfferingIds,
  ]),
});

/**
 * Apply a group write to a cached group list, mirroring what the backend does
 * with it: the written group takes the payload's membership verbatim, and every
 * other group gives up any id the payload claims — an offering belongs to
 * exactly one group, so listing it on one group moves it off whichever group
 * held it before.
 *
 * Groups the write doesn't touch are returned by reference so their rows don't
 * re-render.
 */
export const applyQuoteOfferingGroupMembership = (
  groups: QuoteOfferingGroup[],
  {
    quoteOfferingGroupId,
    quoteOfferingIds,
  }: { quoteOfferingGroupId: string; quoteOfferingIds: string[] },
): QuoteOfferingGroup[] =>
  groups.map((group) => {
    if (group.id === quoteOfferingGroupId) {
      return { ...group, quoteOfferingIds };
    }
    const retainedIds = group.quoteOfferingIds.filter(
      (memberId) => !quoteOfferingIds.includes(memberId),
    );
    return retainedIds.length === group.quoteOfferingIds.length
      ? group
      : { ...group, quoteOfferingIds: retainedIds };
  });

/**
 * Rendering membership: the group's quoteOfferingIds list is the single
 * source of truth (its query is invalidated on every group mutation, unlike
 * the offering's groupId which goes stale until the next quote fetch).
 */
export const findQuoteOfferingGroupForOffering = (
  quoteOffering: Maybe<IQuoteOfferingRespSchema>,
  groups: Maybe<QuoteOfferingGroup[]>,
) =>
  quoteOffering
    ? (groups || []).find(({ quoteOfferingIds }) =>
        quoteOfferingIds.includes(quoteOffering.id),
      )
    : undefined;

/**
 * Partition ordered quote offerings into group containers and ungrouped runs.
 *
 * - Membership derives from group.quoteOfferingIds (see
 *   findQuoteOfferingGroupForOffering); ids that don't resolve to a live
 *   top-level offering are ignored.
 * - If two groups claim the same offering, the first group in the array wins.
 * - Groups with no live member offerings are appended last with an empty
 *   offerings list so they can render as empty containers.
 * - With no groups, returns a single ungrouped partition (flat table).
 */
export const partitionQuoteOfferingsByGroup = (
  quoteOfferings: QuoteOfferingWithChildren[],
  groups?: Maybe<QuoteOfferingGroup[]>,
): QuoteOfferingPartition[] => {
  if (!groups?.length) {
    return [{ group: null, offerings: [...quoteOfferings] }];
  }

  const partitions: QuoteOfferingPartition[] = [];
  const partitionByGroupId = new Map<string, QuoteOfferingPartition>();

  quoteOfferings.forEach((offeringWithChildren) => {
    const group = findQuoteOfferingGroupForOffering(
      offeringWithChildren.quoteOffering,
      groups,
    );
    if (group) {
      let partition = partitionByGroupId.get(group.id);
      if (!partition) {
        partition = { group, offerings: [] };
        partitionByGroupId.set(group.id, partition);
        partitions.push(partition);
      }
      partition.offerings.push(offeringWithChildren);
    } else {
      const lastPartition = partitions[partitions.length - 1];
      if (lastPartition && lastPartition.group === null) {
        lastPartition.offerings.push(offeringWithChildren);
      } else {
        partitions.push({ group: null, offerings: [offeringWithChildren] });
      }
    }
  });

  groups.forEach((group) => {
    if (!partitionByGroupId.has(group.id)) {
      partitions.push({ group, offerings: [] });
    }
  });

  return partitions;
};

/**
 * Groups whose quoteOfferingIds resolve to zero live top-level offerings.
 * These render as empty containers and block quote submission.
 */
export const getEmptyQuoteOfferingGroups = (
  groups: Maybe<QuoteOfferingGroup[]>,
  quoteOfferings: Maybe<IQuoteOfferingRespSchema[]>,
): QuoteOfferingGroup[] => {
  if (!groups?.length) {
    return [];
  }
  const topLevelOfferingIds = new Set(
    (quoteOfferings || [])
      .filter(({ parentQuoteOfferingId }) => !parentQuoteOfferingId)
      .map(({ id }) => id),
  );
  return groups.filter(
    (group) =>
      !group.quoteOfferingIds.some((quoteOfferingId) =>
        topLevelOfferingIds.has(quoteOfferingId),
      ),
  );
};

export type QuoteOfferingGroupContainerPlan = {
  /** One entry per group container to render, in the quote's group order */
  groupPartitions: (QuoteOfferingPartition & { group: QuoteOfferingGroup })[];
  /** Everything in no group, collected into one run */
  ungroupedOfferings: QuoteOfferingWithChildren[];
};

/**
 * The containers a single run of offerings renders: a partition per group with
 * members in this run, ordered by the quote's group list, plus one ungrouped
 * run.
 *
 * A group with no members in this run only gets a container when its id is in
 * `emptyGroupIds`, and the caller decides what "empty" means. That distinction
 * is the whole point of this function: the amendment layout renders the table
 * in two sections, and partitioning a section's slice on its own makes every
 * group that isn't in that slice look empty. `emptyGroupIds` has to be computed
 * against the whole quote (see getEmptyQuoteOfferingGroups), so a group whose
 * offerings all sit in another section doesn't render a second, empty container.
 */
export const planQuoteOfferingGroupContainers = ({
  offerings,
  groups,
  emptyGroupIds,
}: {
  offerings: QuoteOfferingWithChildren[];
  groups: Maybe<QuoteOfferingGroup[]>;
  /** Groups allowed to render an empty container; omit to render none */
  emptyGroupIds?: Set<string>;
}): QuoteOfferingGroupContainerPlan => {
  const groupOrderById = new Map(
    (groups || []).map(({ id }, index) => [id, index]),
  );
  const partitions = partitionQuoteOfferingsByGroup(offerings, groups);

  return {
    groupPartitions: partitions
      .filter(
        (
          partition,
        ): partition is QuoteOfferingPartition & {
          group: QuoteOfferingGroup;
        } =>
          !!partition.group &&
          (partition.offerings.length > 0 ||
            !!emptyGroupIds?.has(partition.group.id)),
      )
      .sort(
        (a, b) =>
          (groupOrderById.get(a.group.id) ?? 0) -
          (groupOrderById.get(b.group.id) ?? 0),
      ),
    ungroupedOfferings: partitions
      .filter(({ group }) => !group)
      .flatMap(({ offerings: partitionOfferings }) => partitionOfferings),
  };
};

export const getQuoteOfferingGroupLabel = (group: QuoteOfferingGroup) =>
  `Ship To: ${group.shippingAccount.accountName} | Bill To: ${group.billingAccount.accountName}`;

/**
 * Total for a run of offerings, ramp/scheduled-change segments included —
 * the group container's subtotal, and the same arithmetic
 * QuoteOfferingAmount does for a single row.
 */
export const getQuoteOfferingsSubtotal = (
  offerings: Maybe<QuoteOfferingWithChildren[]>,
) =>
  (offerings || []).reduce(
    (total, { quoteOffering, children }) =>
      total +
      (quoteOffering?.amount || 0) +
      (children || []).reduce((sum, { amount }) => sum + (amount || 0), 0),
    0,
  );

/**
 * A group subtotal as the string its header shows — on the quote's own group
 * containers and on the cards in the Manage Groups modal.
 *
 * Capped at the currency's precision (cents, or whole yen), because
 * formatCurrency defaults to as many decimals as it takes to render the number
 * exactly — and a sum of prorated amounts is rarely a round one (three shares
 * of 1200/7 renders as $514.2857142857142).
 */
export const formatQuoteSubtotal = (amount: number, currency: Maybe<string>) =>
  formatCurrency(amount, {
    ...(currency ? { currency } : {}),
    maximumFractionDigits: getCurrencyDecimals(currency),
  });

/** The subtotal above, for a run of offerings from the quote's table */
export const formatQuoteOfferingsSubtotal = (
  offerings: Maybe<QuoteOfferingWithChildren[]>,
  currency: Maybe<string>,
) => formatQuoteSubtotal(getQuoteOfferingsSubtotal(offerings), currency);

/**
 * Check if quote offering is added to amendment quote or not
 * @param quoteOffering QuoteOffering
 * @returns
 */
export const isQuoteOfferingAddedToAmendment = (
  quoteOffering: IQuoteOfferingRespSchema,
) => {
  return quoteOffering?.items?.every((item) => {
    return item.amendmentStatus === QuoteItemAmendmentStatusEnum.ADDED;
  });
};

/**
 * What the current user may change about a quote's offering groups. Editing at
 * all requires an editable DRAFT; amendments and renewals then restrict it
 * further, because their existing offerings and groups mirror the contract
 * being amended and are not the amendment's to rearrange.
 */
export type QuoteOfferingGroupPermissions = {
  /** View only — no group may be added, changed, or deleted */
  isReadOnly: boolean;
  /** New groups may be added */
  canAddGroup: boolean;
  /**
   * Only offerings added on this quote may change groups. Set on amendments and
   * renewals, where the rest carry over from the contract.
   */
  allowsMovingNewOfferingsOnly: boolean;
};

export const getQuoteOfferingGroupPermissions = ({
  quote,
  isReadOnly,
}: {
  quote: Maybe<IQuoteRespSchema>;
  /** The quote page's read-only state (no edit permission, or not editable) */
  isReadOnly: boolean;
}): QuoteOfferingGroupPermissions => {
  const canEdit = !isReadOnly && quote?.status === QuoteStatusEnum.DRAFT;
  const isAmendmentOrRenewal = !!quote && quote.type !== QuoteTypeEnum.NEW;

  return {
    isReadOnly: !canEdit,
    canAddGroup: canEdit,
    allowsMovingNewOfferingsOnly: canEdit && isAmendmentOrRenewal,
  };
};

/**
 * Whether a group was added on this quote rather than carried over from the one
 * being amended or renewed. `referenceGroupId` points at the group it was copied
 * from, so it is set on exactly the carried-over groups — and never on a NEW
 * quote, where every group is the quote's own.
 *
 * This is the whole rule: the API rejects deleting a carried-over group or
 * re-pointing its accounts, because both belong to offerings the contract
 * already has. Contacts stay editable on either kind. An unsaved draft row has
 * no reference, so it counts as added here too.
 */
export const isQuoteOfferingGroupAddedOnQuote = (
  group: Maybe<Pick<QuoteOfferingGroup, 'referenceGroupId'>>,
) => !group?.referenceGroupId;

/**
 * Whether an offering may be moved between groups. On amendments and renewals
 * that is limited to offerings the amendment itself added — everything else
 * belongs to the contract being amended.
 */
export const canMoveQuoteOfferingBetweenGroups = (
  quoteOffering: Maybe<IQuoteOfferingRespSchema>,
  { isReadOnly, allowsMovingNewOfferingsOnly }: QuoteOfferingGroupPermissions,
) => {
  if (isReadOnly || !quoteOffering) {
    return false;
  }
  return (
    !allowsMovingNewOfferingsOnly ||
    isQuoteOfferingAddedToAmendment(quoteOffering)
  );
};

/**
 * Get abbreviated unit price frequency display
 */
export const getShortCodeForUnitPriceFrequency = (
  displayUnitPriceFrequency?: Maybe<number>,
) => {
  if (!displayUnitPriceFrequency || displayUnitPriceFrequency === 1) {
    return 'mo';
  }
  if (displayUnitPriceFrequency === 12) {
    return 'yr';
  }
  if (displayUnitPriceFrequency % 12 === 0) {
    return `${displayUnitPriceFrequency / 12}yr`;
  }
  return `${displayUnitPriceFrequency}mo`;
};

/**
 * Get non-abbreviated unit price frequency display
 */
export const getLongCodeForUnitPriceFrequency = (
  displayUnitPriceFrequency?: Maybe<number>,
) => {
  if (!displayUnitPriceFrequency || displayUnitPriceFrequency === 1) {
    return 'month';
  }
  if (displayUnitPriceFrequency === 12) {
    return 'year';
  }
  if (displayUnitPriceFrequency % 12 === 0) {
    return `${displayUnitPriceFrequency / 12} years`;
  }
  return `${displayUnitPriceFrequency} months`;
};

export const getPricePerUnitWithRateFrequencyShortCode = ({
  priceStr,
  productType,
  isCustomPrice,
  item,
  offeringRate,
}: {
  priceStr: string;
  productType?: ProductTypeEnum;
  isCustomPrice?: boolean;
  item?: Pick<IQuoteItemRespSchema, 'options' | 'productId'>;
  offeringRate?: Pick<
    IRateResSchema,
    | 'billingFrequency'
    | 'billingFrequencyInMonths'
    | 'currency'
    | 'usageBillingFrequency'
    | 'prices'
  > | null;
}): string => {
  const { billingFrequency, billingFrequencyInMonths } = offeringRate || {
    billingFrequency: null,
    billingFrequencyInMonths: null,
    usageBillingFrequency: null,
    prices: [],
  };
  const { options } = item || {
    options: null,
    productId: '',
  };

  if (
    !billingFrequency ||
    (productType && ONE_TIME_PRODUCT_TYPES.has(productType)) ||
    (productType && ONETIME_TOKEN_PRODUCT_TYPES.has(productType))
  ) {
    return priceStr;
  }

  if (productType === ProductTypeEnum.USAGE) {
    const pricingModel = offeringRate?.prices.find(
      (price) => price.product.id === item?.productId,
    )?.priceModel;

    if (pricingModel === PriceModelEnum.FLAT) {
      return priceStr === '*' ? '*/mo' : `${priceStr}`;
    }

    return priceStr === '*' ? '*/mo' : `${priceStr}/unit`;
  }

  const shortCode =
    RATE_BILLING_FREQUENCY_SHORTCODE_DISPLAY[billingFrequency](
      billingFrequencyInMonths,
    ) || '';
  if (options && options.displayUnitPriceFrequency) {
    return `${priceStr}/${getShortCodeForUnitPriceFrequency(
      options.displayUnitPriceFrequency,
    )}`;
  } else if (shortCode) {
    return `${priceStr}/${shortCode}`;
  } else {
    return priceStr;
  }
};

export const getBlockPricing = ({
  item,
  offeringRate,
}: {
  item?: Pick<IQuoteItemRespSchema, 'productId'>;
  offeringRate?: Pick<IRateResSchema, 'prices'> | null;
}): string | null => {
  const { prices } = offeringRate || {
    prices: [],
  };

  const matchingPrices = prices.find((price) => {
    return price.product.id === item?.productId && price.blockSize !== null;
  });

  if (matchingPrices) {
    return `(per ${matchingPrices.blockSize})`;
  }

  return null;
};

export const getQuoteColumnAvailabilityReview = (
  productType: ProductTypeEnum,
) => {
  // Disable discount for MIN_COMMIT and ONETIME_PREPAID_CREDIT
  const isDiscountNotAvailable = [
    ProductTypeEnum.MIN_COMMIT,
    ProductTypeEnum.ONETIME_PREPAID_CREDIT,
    ProductTypeEnum.DISCOUNT,
  ].includes(productType);

  // Disable quantity for USAGE and MIN_COMMIT
  const isQuantityNotAvailable = [ProductTypeEnum.USAGE].includes(productType);

  return { isDiscountNotAvailable, isQuantityNotAvailable };
};

export const getQuoteColumnAvailability = (productType: ProductTypeEnum) => {
  // Disable discount for MIN_COMMIT and ONETIME_PREPAID_CREDIT
  const isDiscountNotAvailable = [
    ProductTypeEnum.MIN_COMMIT,
    ProductTypeEnum.ONETIME_PREPAID_CREDIT,
  ].includes(productType);

  // Disable quantity for USAGE and MIN_COMMIT
  const isQuantityNotAvailable = [
    ProductTypeEnum.USAGE,
    ProductTypeEnum.MIN_COMMIT,
  ].includes(productType);

  // Disable quantity for MIN_COMMIT
  const isPPUNotAvailable = [ProductTypeEnum.MIN_COMMIT].includes(productType);

  return { isDiscountNotAvailable, isQuantityNotAvailable, isPPUNotAvailable };
};

export const getQuoteOfferingDiscountAvailability = (
  quoteOffering?: IQuoteOfferingRespSchema | null,
) => {
  if (!quoteOffering) {
    return {
      isQuoteOfferingDiscountAvailable: false,
    };
  }

  const isQuoteOfferingDiscountAvailable = !(
    quoteOffering.items.length === 1 &&
    (quoteOffering.items[0].productType === ProductTypeEnum.MIN_COMMIT ||
      quoteOffering.items[0].productType ===
        ProductTypeEnum.ONETIME_PREPAID_CREDIT)
  );
  return { isQuoteOfferingDiscountAvailable };
};

export const getVariableAmountIndicator = (variableAmount?: boolean): string =>
  variableAmount ? '*' : '';

export function sortQuoteApprovals<
  T extends { rank: number; createDate: string | null },
>(approvals: T[]) {
  return (
    approvals?.sort((v1, v2) => {
      if (v1.rank === v2.rank) {
        return (
          new Date(v1.createDate || '').getTime() -
          new Date(v2.createDate || '').getTime()
        );
      }
      return v1.rank - v2.rank;
    }) || []
  );
}

/**
 * Get the name of the first unapproved approver
 * @param quote quote object
 * @returns name of the first unapproved quote approver, or empty string if all are approved
 */
export const whoIsFirstQuoteApproval = (quote: IQuoteRespSchema | null) => {
  if (!Array.isArray(quote?.approvals) || quote.approvals.length === 0) {
    return '';
  }

  const sortedApprovals = sortQuoteApprovals(quote.approvals);

  // Find the first approval that is not yet approved
  const firstUnapprovedApproval = sortedApprovals.find(
    (approval) => approval.status !== ApprovalStatusEnum.APPROVED,
  );

  if (!firstUnapprovedApproval) {
    return '';
  }

  return (
    (firstUnapprovedApproval.teamId
      ? firstUnapprovedApproval.teamName
      : firstUnapprovedApproval.username) || ''
  );
};

/**
 * Transform IQuoteRespSchema into IQuoteRequestSchema
 */
export const getQuoteRequestFromQuoteResponse = (
  quote: IQuoteRespSchema,
): IQuoteRequestSchema => {
  const quoteRequest = pick(
    quote,
    Object.keys(QuoteRequestSchema.shape),
  ) as IQuoteRequestSchema;

  /** Set all other fields that differ between data structures */
  quoteRequest.opportunityId = quote.opportunity?.id;
  quoteRequest.legalEntityId = quote.fromCompany?.legalEntityId;

  return quoteRequest;
};

/**
 *
 * Based on the customContractLengthInputVisibility, amendment, renewal
 * it returns gridTemplateColumns
 */
export function getQuoteFormHeaderGridTemplateColumns({
  isAmendment,
  isRenewal,
  useAmendmentV2,
}: {
  isAmendment: boolean;
  isRenewal: boolean;
  useAmendmentV2: boolean;
}) {
  if (isAmendment) {
    if (!useAmendmentV2) {
      return '7.875rem minmax(10rem, 12.5rem) 10rem 9.375rem 6rem 9.75rem 3.75rem';
    } else {
      return 'minmax(10rem, 12.5rem) 10rem 9.375rem 6rem 9.75rem 3.75rem';
    }
  }
  return `minmax(10rem, 12.5rem) 10rem 9.375rem 6rem 9.75rem 3.75rem`;
}

/**
 * Determine all the fields that have been modified in a quote offering
 * By comparing the payload and the existing state
 *
 * Certain fields on the form don't have reliable dirty detection and other fields have complex data to determine if modified
 * This function handles all of that complexity
 *
 * @param newOffering
 * @param existingQuoteOffering
 * @param overrideOfferingDirtyFields Header level (offering) fields that should be marked as dirty even if same
 *                             This is generally used for changes to related entities, such as Rate
 */
export function calculateModifiedQuoteItemFields(
  newOffering: IQuoteOfferingReqSchema,
  existingQuoteOffering: IQuoteOfferingRespSchema,
  overrideOfferingDirtyFields?: string[],
): Map<string, Set<string>> {
  try {
    const output = new Map<string, Set<string>>([
      ['header', new Set()], // QuoteOffering fields
      ...newOffering.items.map((item): [string, Set<string>] => [
        item.id!,
        new Set<string>(),
      ]),
    ]);

    if (overrideOfferingDirtyFields) {
      overrideOfferingDirtyFields.forEach((field) =>
        output.get('header')?.add(field),
      );
    }

    /**
     * Compare quote offering fields to see if anything is dirty
     */
    quoteOfferingComparison.offering.fieldsToCompare.forEach((field) => {
      compareField(
        output.get('header')!,
        newOffering,
        existingQuoteOffering,
        field,
        quoteOfferingComparison.offering.comparisonFns,
      );
    });

    /**
     * Triggering dirty quote item fields for optional products removal
     */

    if (newOffering.items.length < existingQuoteOffering.items.length) {
      existingQuoteOffering.items.forEach((existingItem) => {
        const newQuoteItem = newOffering.items.find(
          ({ id }) => id === existingItem.id,
        );
        if (isUndefined(newQuoteItem)) {
          output.set(
            existingItem.id,
            new Set<string>(quoteOfferingComparison.item.fieldsToCompare),
          );
        }
      });
      return output;
    }

    /**
     * Compare quote item fields to see if anything is dirty
     */
    newOffering.items.forEach((newQuoteItem) => {
      const existingItem = existingQuoteOffering.items.find(
        ({ id }) => id === newQuoteItem.id,
      );
      quoteOfferingComparison.item.fieldsToCompare.forEach((field) => {
        compareField(
          output.get(newQuoteItem.id!)!,
          newQuoteItem,
          existingItem,
          field,
          quoteOfferingComparison.item.comparisonFns,
        );
      });
    });
    return output;
  } catch (ex) {
    logger.warn('Error calculating quoteOfferingComparison', ex);
    return new Map();
  }
}

/**
 * BP-6928
 * Determine if a quote offering is allowed to make a specific rate change
 * For scheduled changes, the rate is not allowed to change billing frequency
 *
 * @param ratesById
 * @param oldRateId
 * @param newRateId
 * @param isPartOfScheduledChange
 * @returns
 */
export function isOfferingRateChangeAllowed({
  ratesById,
  oldRateId,
  newRateId,
  isPartOfScheduledChange,
}: {
  ratesById: Record<string, IRateResBaseSchema>;
  oldRateId: string;
  newRateId: string;
  isPartOfScheduledChange: boolean;
}): boolean {
  const oldRate = ratesById[oldRateId];
  const newRate = ratesById[newRateId];
  if (
    !isPartOfScheduledChange ||
    oldRateId === newRateId ||
    !oldRate ||
    !newRate
  ) {
    return true;
  }

  return (
    oldRate.billingFrequencyInMonths === newRate.billingFrequencyInMonths &&
    oldRate.subscriptionTiming === newRate.subscriptionTiming
  );
}

/**
 * Perform comparison of field
 * @param outputSet Set to add dirty fields to
 * @param newObject Quote Offering or Quote Item request
 * @param previousObj Quote Offering or Quote Item response to compare against
 * @param field Field to compare on old an new object
 * @param comparisonFns For complex comparisons, object with field as the key and comparison function as the value
 */
function compareField(
  outputSet: Set<string>,
  newObject: any,
  previousObj: any,
  field: keyof IQuoteOfferingReqSchema | keyof IQuoteItemReqSchema,
  comparisonFns: Record<string, (oldObj: any, newObj: any) => boolean>,
) {
  try {
    comparisonFns = comparisonFns || {};
    const comparisonFn = comparisonFns[field];

    if (!previousObj) {
      // This is a new offering, flag every field as modified
      outputSet?.add(field);
    } else if (comparisonFn) {
      // Call comparisonFn to see if field should be considered as modified
      if (comparisonFn(newObject, previousObj)) {
        outputSet?.add(field);
      }
    } else if (
      // Perform strict equality check to see if field is modified
      // eslint-disable-next-line eqeqeq
      newObject[field] != previousObj[field]
    ) {
      outputSet?.add(field);
    }
  } catch (ex) {
    logger.warn('Error comparing field', field, ex);
  }
}

/**
 * Comparison functions for quote offering and quote item
 * This is used to detect if there are any changes
 * LOGIC:
 * * All fields will be compared unless included in QUOTE_OFFERING_FIELDS_COMPARISON_IGNORE OR QUOTE_ITEM_FIELDS_COMPARISON_IGNORE
 * * Comparison will be a simple comparison using `!==` unless the field has a comparisonFns, in which case that will be used
 */

const QUOTE_OFFERING_FIELDS_COMPARISON_IGNORE = new Set([
  'id', // this will never change
  'offeringId', // this will never change
  'subscriptionId', // this will never change
  'items', // compared separately
]);

const QUOTE_ITEM_FIELDS_COMPARISON_IGNORE = new Set([
  'id', // this will never change
  'customId', // this will never change
  'productId', // this will never change
  'sku', // this will never change
  'customDiscountType', // discounts handled by customDiscountAmountOrPercent
  'customDiscountName', // discounts handled by customDiscountAmountOrPercent
  'customDiscountDescription', // discounts handled by customDiscountAmountOrPercent
]);

/**
 * Comparison object
 * fieldsToCompare -> These are all the fields on the data model to compare to detect if a field changed
 * comparisonFns -> For complex fields, these are comparison functions to be used instead of `!==`
 */
const quoteOfferingComparison = {
  offering: {
    fieldsToCompare: Object.keys(QuoteOfferingReqSchema.shape).filter(
      (field) => !QUOTE_OFFERING_FIELDS_COMPARISON_IGNORE.has(field),
    ) as (keyof IQuoteOfferingReqSchema)[],
    /**
     * IF the field on the request and response cannot be compared with !==
     * then add comparison function here
     */
    comparisonFns: {
      discountIds: (
        newOffering: IQuoteOfferingReqSchema,
        existingQuoteOffering: IQuoteOfferingRespSchema,
      ): boolean => {
        if (!newOffering.discountIds) {
          return false;
        }
        const discountIds = new Set(newOffering.discountIds);
        const modifiedDiscount =
          newOffering.discountIds.length !==
            existingQuoteOffering.discounts.length ||
          existingQuoteOffering.discounts.some(
            (discount) => !discountIds.has(discount.id),
          );
        return modifiedDiscount;
      },
      schedule: (
        newOffering: IQuoteOfferingReqSchema,
        existingQuoteOffering: IQuoteOfferingRespSchema,
      ): boolean => {
        return (
          !!newOffering.schedule &&
          (newOffering.schedule?.startDate !==
            existingQuoteOffering.startDate ||
            newOffering.schedule?.endDate !== existingQuoteOffering.endDate)
        );
      },
      /**
       * `options` is an object, so the default `!=` would compare references
       * and report a change on every save. Compare the settings it holds.
       */
      options: (
        newOffering: IQuoteOfferingReqSchema,
        existingQuoteOffering: IQuoteOfferingRespSchema,
      ): boolean => {
        return (
          newOffering.options?.priceDisplay !==
          existingQuoteOffering.options?.priceDisplay
        );
      },
    },
  },
  item: {
    fieldsToCompare: Object.keys(QuoteItemReqSchema.shape).filter(
      (field) => !QUOTE_ITEM_FIELDS_COMPARISON_IGNORE.has(field),
    ) as (keyof IQuoteItemReqSchema)[],
    comparisonFns: {
      customDiscountAmountOrPercent: (
        newItem: IQuoteItemReqSchema,
        existingItem: IQuoteItemRespSchema,
      ): boolean => {
        if (
          newItem.customDiscountType !== existingItem.customDiscountType ||
          newItem.customDiscountName !== existingItem.customDiscountName ||
          newItem.customDiscountDescription !==
            existingItem.customDiscountDescription ||
          // eslint-disable-next-line eqeqeq -- this value is converted from string to number so strict equality check gives a false positive
          newItem.customDiscountAmountOrPercent !=
            existingItem.customDiscountAmountOrPercent
        ) {
          return true;
        }
        return false;
      },
      customPrice: (
        newItem: IQuoteItemReqSchema,
        existingItem: IQuoteItemRespSchema,
      ): boolean => {
        return (
          !!newItem.customPrice &&
          newItem.customPrice.unitAmount !== existingItem.unitPrice
        );
      },
      options: (
        newItem: IQuoteItemReqSchema,
        existingItem: IQuoteItemRespSchema,
      ): boolean => {
        const newItemOptions: any = newItem.options || {};
        const existingItemOptions: any = existingItem.options || {};
        return Object.keys(newItem.options || {}).some((option) => {
          return !isEqual(newItemOptions[option], existingItemOptions[option]);
        });
      },
      customDiscountLifecyclePolicies: (
        newItem: IQuoteItemReqSchema,
        existingItem: IQuoteItemRespSchema,
      ): boolean => {
        const newPolicies = newItem.customDiscountLifecyclePolicies || [];
        const existingPolicies =
          existingItem.customDiscountLifecyclePolicies || [];

        // Use lodash isEqual for deep array comparison
        return !isEqual(newPolicies, existingPolicies);
      },
    },
  },
} as const;

export const hasQuoteDiscount = (quote?: IQuoteRespSchema | null): boolean => {
  if (!quote) {
    return false;
  }
  if (quote.discountAmount) {
    return true;
  }
  const quoteItems = quote.quoteOfferings.flatMap(
    ({ items }) => items,
  ) as IQuoteItemRespSchema[];

  const hasDiscountInQuoteItems = quoteItems.some(
    ({
      customDiscountAmountOrPercent,
      discountAmount,
      discountPercent,
      discounts,
    }) => {
      return (
        Boolean(customDiscountAmountOrPercent) ||
        Boolean(discountAmount) ||
        Boolean(discountPercent) ||
        Boolean(discounts.length)
      );
    },
  );
  return hasDiscountInQuoteItems;
};

export const getUnitPriceBeforeDiscount = (
  item: IQuoteItemRespSchema,
  quotePrices: IQuotePrice[],
  currency: string,
  rateId?: string,
  parentQuoteOfferingId?: string,
  displayPrecision?: Maybe<number>,
): string => {
  const maximumFractionDigits = displayPrecision ?? 2;
  // For usage, get the discount information from the quote prices
  if (
    item.productType === ProductTypeEnum.USAGE &&
    rateId &&
    quotePrices?.length
  ) {
    const prices = getQuotePricesForQuoteItem({
      parentQuoteOfferingId,
      quoteOfferingId: item.quoteOfferingId,
      quotePrices,
      productId: item.productId,
      rateId,
    });

    const hasDiscount =
      item.discounts.length > 0 || !!item.customDiscountAmountOrPercent;
    if (prices.length === 1 && prices[0].amount && hasDiscount) {
      return formatCurrency(prices[0].amount, {
        currency,
        maximumFractionDigits,
      });
    }
  }

  if (
    item &&
    !isNil(item.unitPrice) &&
    !isNil(item.unitPriceAfterDiscount) &&
    item.unitPrice - item.unitPriceAfterDiscount > 0
  ) {
    if (item.options && Boolean(item.options.displayUnitPriceFrequency)) {
      return formatCurrency(item.displayUnitPrice, {
        currency,
        maximumFractionDigits,
      });
    }
    return formatCurrency(item.unitPrice, {
      currency,
      maximumFractionDigits,
    });
  }
  return '';
};

/**
 * Get the unit price after discount as a formatted string,
 * optionally applying an uplift percentage
 */
export const getUnitPriceAfterDiscount = ({
  parentQuoteOfferingId,
  item,
  quotePrices,
  currency,
  offeringId,
  rateId,
  displayPrecision = 2,
  isSequential = false,
}: {
  parentQuoteOfferingId?: string;
  item: IQuoteItemRespSchema;
  quotePrices: IQuotePrice[];
  currency: string;
  offeringId?: string;
  rateId?: string;
  displayPrecision?: Maybe<number>;
  isSequential?: boolean;
}) => {
  const maximumFractionDigits = displayPrecision ?? 2;
  // For usage, get the discount information from the quote prices
  if (item.productType === ProductTypeEnum.USAGE) {
    if (offeringId && rateId) {
      const prices = getQuotePricesForQuoteItem({
        parentQuoteOfferingId,
        quoteOfferingId: item.quoteOfferingId,
        quotePrices,
        productId: item.productId,
        rateId,
      });
      if (prices.length === 1 && prices[0]) {
        const discountPercent = getDiscountPercentForQuoteItem(
          item,
          isSequential,
        );
        return formatCurrency(prices[0].amount * (1 - discountPercent), {
          currency,
          maximumFractionDigits,
          minimumFractionDigits: Math.min(2, maximumFractionDigits),
        });
      }
    }
    return '*';
  }

  if (item.options && Boolean(item.options.displayUnitPriceFrequency)) {
    const hasDiscount =
      Boolean(item.customDiscountAmountOrPercent) ||
      Boolean(item.discountAmount) ||
      Boolean(item.discountPercent) ||
      Boolean(item.discounts.length);

    if (hasDiscount) {
      return formatCurrency(item.displayUnitPriceAfterDiscount, {
        currency,
        maximumFractionDigits,
      });
    }

    return formatCurrency(item.displayUnitPrice, {
      currency,
      maximumFractionDigits,
    });
  }

  return formatCurrency(item.displayUnitPriceAfterDiscount, {
    currency,
    maximumFractionDigits,
  });
};

/**
 * Manually calculate the combined value of custom and catalog discounts
 * This is required for usage products if we need to know the total discount percent, since discountPercent is zero
 *
 * FIXME: we need a shared utils library that is not a dependency of "types/app/core" so we can put things like this there :sob:
 * this is used on PDF service as well
 */
export const getDiscountPercentForQuoteItem = (
  item: Pick<
    IQuoteItemRespSchema,
    'customDiscountAmountOrPercent' | 'discounts'
  >,
  isSequential = false,
) => {
  const catalogTotal = item.discounts.reduce(
    (acc, { discountAmountOrPercent }) => acc + (discountAmountOrPercent || 0),
    0,
  );
  const customDiscount = item.customDiscountAmountOrPercent || 0;

  if (isSequential) {
    return Math.min(
      1 - (1 - customDiscount / 100) * (1 - catalogTotal / 100),
      1,
    );
  }

  return Math.min((catalogTotal + customDiscount) / 100, 1);
};

export const getDisplayUnitPriceFrequencyDefaultValue = (
  billingFrequency: RateBillingFrequencyEnum,
  displayUnitPriceFrequency?: number | null,
): number => {
  const defaultFrequencyMap: Record<RateBillingFrequencyEnum, number> = {
    [RateBillingFrequencyEnum.MONTHLY]: 1,
    [RateBillingFrequencyEnum.QUARTERLY]: 3,
    [RateBillingFrequencyEnum.SEMIANNUALLY]: 6,
    [RateBillingFrequencyEnum.ANNUALLY]: 12,
    // NOTE: purposefully set to 1 so that I don't have to deal with them and they all are being ignore
    [RateBillingFrequencyEnum.DAILY]: 1,
    [RateBillingFrequencyEnum.CUSTOM]: 1,
    [RateBillingFrequencyEnum.ONETIME]: 1,
  };

  return displayUnitPriceFrequency ?? defaultFrequencyMap[billingFrequency];
};

export const getQuotePricesForQuoteItem = ({
  quoteOfferingId,
  quotePrices,
  productId,
  rateId,
  parentQuoteOfferingId,
}: {
  quoteOfferingId: string;
  quotePrices: IQuotePrice[];
  productId: string;
  rateId: string;
  parentQuoteOfferingId?: string;
}) => {
  return (
    quotePrices
      ?.filter(
        (price) =>
          price.quoteOfferingId === quoteOfferingId ||
          price.quoteOfferingId === parentQuoteOfferingId,
      )
      .flatMap((item) => item.schedule)
      .find(
        (schedule) =>
          schedule.rate.id === rateId &&
          schedule.quoteOfferingId === quoteOfferingId,
      )
      ?.productPrices.filter(
        (productPrice) => productPrice.product.id === productId,
      )?.[0]?.prices || []
  );
};

export const buildContractOptions = (
  contracts: IContract[],
  quotes: IQuoteRespSchema[],
): IContractWithQuotes[] => {
  const validContracts = contracts.filter(({ renewed }) => !renewed);

  const latestQuoteByContractId = groupBy(
    quotes.filter(
      ({ type, status: quoteStatus }) =>
        (type === QuoteTypeEnum.NEW || type === QuoteTypeEnum.RENEWAL) &&
        quoteStatus === QuoteStatusEnum.PROCESSED,
    ),
    'contractId',
  );

  const contractWithQuotes = validContracts.map((contract) => ({
    ...contract,
    quotes:
      latestQuoteByContractId[contract.id] &&
      latestQuoteByContractId[contract.id].length
        ? latestQuoteByContractId[contract.id]
        : [],
  }));

  return contractWithQuotes;
};

export const isContractLengthWholeYear = (contractLength: number): boolean => {
  return contractLength % 12 === 0;
};

export const getContractLengthInMonthOrYear = (
  isCustomContractLength: boolean,
  contractLength: number,
): number => {
  if (!isCustomContractLength && contractLength % 12 === 0) {
    return contractLength / 12; //As api return contractLength value in months so converting to Year
  }

  return contractLength;
};

export const getPriceDisplayWithFrequency = ({
  price,
  productType,
  priceFrequency,
  billingFrequency,
  pricingModel,
}: {
  price: string;
  productType: ProductTypeEnum;
  priceFrequency?: number;
  billingFrequency: number;
  pricingModel?: PriceModelEnum;
}): string => {
  if (ONE_TIME_PRODUCT_TYPES.has(productType)) {
    return `${price}`;
  }

  if (productType === ProductTypeEnum.USAGE) {
    if (pricingModel && pricingModel === PriceModelEnum.FLAT) {
      return price;
    }

    return `${price}/unit`;
  }

  if (!priceFrequency) {
    return `${price}/${getShortCodeForUnitPriceFrequency(billingFrequency)}`;
  }

  return `${price}/${getShortCodeForUnitPriceFrequency(priceFrequency)}`;
};

export const getRelevantOfferingStartDateForAnnualMinCommit = (
  isAmendment: boolean,
  quote: IQuoteRespSchema,
  currentOffering: IQuoteOfferingReqSchema,
): string => {
  const todaysDate = new Date();
  const contractStartDate = parseISO(quote.contractStartDate);
  const contractEndDate = parseISO(quote.contractEndDate);
  const hasExplicitOfferingStartDate = !!currentOffering.startDate;

  const anchorDay = quote.billingAnchor?.day;

  const snapToAnchorOrClamp = (date: Date): string => {
    const snapped = anchorDay ? getRelevantDayInMonth(anchorDay, date) : date;
    return isBefore(snapped, contractStartDate)
      ? toDateOnly(quote.contractStartDate, 'userTimezone')
      : toDateOnly(format(snapped, 'yyyy-MM-dd'), 'userTimezone');
  };

  // Non-amendment: explicit date drives the result; fall back to contract start when absent
  if (!isAmendment) {
    if (!hasExplicitOfferingStartDate) {
      return toDateOnly(quote.contractStartDate, 'userTimezone');
    }
    if (!anchorDay) {
      return toDateOnly(currentOffering.startDate!, 'userTimezone');
    }
    // Non-amendment + explicit date + billing anchor: snap anchor to the explicit date's month
    return snapToAnchorOrClamp(parseISO(currentOffering.startDate!));
  }

  // Amendment + explicit date + no billing anchor: use it directly
  if (hasExplicitOfferingStartDate && !anchorDay) {
    return toDateOnly(currentOffering.startDate!, 'userTimezone');
  }

  // Amendment: find next annual anniversary >= today.
  // When a billing anchor is set, explicit date is ignored in favour of the annual rhythm.
  let nextAnnualDate = contractStartDate;
  while (isBefore(nextAnnualDate, todaysDate)) {
    nextAnnualDate = addYears(nextAnnualDate, 1);
  }

  if (isBefore(contractEndDate, nextAnnualDate)) {
    return toDateOnly(quote.contractStartDate, 'userTimezone');
  }

  return snapToAnchorOrClamp(nextAnnualDate);
};

export const getRelevantOfferingStartDate = (
  isAmendment: boolean,
  quote: IQuoteRespSchema,
  currentOffering: IQuoteOfferingReqSchema,
  offering?: IOfferingRes,
): string => {
  const todaysDate = new Date();
  const hasExplicitOfferingStartDate = !!currentOffering.startDate;
  if (!quote) {
    return toDateOnly(todaysDate, 'userTimezone');
  }

  // When offering data hasn't loaded yet (e.g. React state not updated after selecting a
  // new offering), fall back to contractStartDate for both past and future contracts rather
  // than today. Using today for a future contract would be before contractStartDate, which
  // is invalid and causes downstream errors.
  if (!offering) {
    if (!hasExplicitOfferingStartDate) {
      return toDateOnly(quote.contractStartDate, 'userTimezone');
    }
    return toDateOnly(todaysDate, 'userTimezone');
  }

  const contractEndDate = parseISO(quote.contractEndDate);
  const isContractInThePast = isBefore(contractEndDate, todaysDate);
  const isContractInTheFuture = isBefore(
    todaysDate,
    parseISO(quote.contractStartDate),
  );

  // If contract is in the past and there is no explicit offering start date, default to contract start date to avoid confusion
  if (isContractInThePast && !hasExplicitOfferingStartDate) {
    return toDateOnly(quote.contractStartDate, 'userTimezone');
  }

  if (isContractInTheFuture && !hasExplicitOfferingStartDate) {
    return toDateOnly(quote.contractStartDate, 'userTimezone');
  }

  // For active backdated contracts (contractStartDate in past, contract not yet expired) with no
  // explicit offering start date and no amendment date to snap to, always return contractStartDate
  // regardless of offering type. This prevents snapping to today or a future billing cycle date.
  const isContractStartDateInThePast = isBefore(
    parseISO(quote.contractStartDate),
    todaysDate,
  );
  if (
    isContractStartDateInThePast &&
    !hasExplicitOfferingStartDate &&
    !isAmendment
  ) {
    return toDateOnly(quote.contractStartDate, 'userTimezone');
  }

  // For min-commit, delegate to specialized handler
  const isMinCommit = offering.type === OfferingTypesEnum.MIN_COMMIT;

  if (isMinCommit) {
    const selectedRate = offering.rates.find(
      (rate) => rate.id === currentOffering.rateId,
    );
    const isAnnualRate =
      selectedRate?.billingFrequency === RateBillingFrequencyEnum.ANNUALLY;

    if (isAnnualRate) {
      return getRelevantOfferingStartDateForAnnualMinCommit(
        isAmendment,
        quote,
        currentOffering,
      );
    }

    // Non-annual min-commit: existing logic unchanged
    if (quote.billingAnchor?.day) {
      const contractStartDate = parseISO(quote.contractStartDate);
      const offeringStartDate = currentOffering.startDate
        ? parseISO(currentOffering.startDate)
        : contractStartDate;
      return getStartDateWithBillingAnchor(
        isAmendment,
        quote.billingAnchor.day,
        contractStartDate,
        offeringStartDate,
        hasExplicitOfferingStartDate,
      );
    }
    return getRelevantOfferingStartDateForUsageOrMinCommit(
      isAmendment,
      quote,
      currentOffering?.startDate,
      isMinCommit,
    );
  }

  if (currentOffering.startDate) {
    return toDateOnly(currentOffering.startDate, 'userTimezone');
  }

  if (isAmendment && !currentOffering.startDate) {
    const contractStartDate = parseISO(quote.contractStartDate);
    const isTodayWithinContract =
      !isBefore(todaysDate, contractStartDate) &&
      !isBefore(contractEndDate, todaysDate);
    return isTodayWithinContract
      ? toDateOnly(todaysDate, 'userTimezone')
      : toDateOnly(quote.contractStartDate, 'userTimezone');
  }

  return toDateOnly(quote.contractStartDate, 'userTimezone');
};

export const getStartDateWithBillingAnchor = (
  isAmendment: boolean,
  anchorDay: number,
  contractStartDate: Date,
  offeringStartDate: Date,
  hasExplicitOfferingStartDate: boolean,
): string => {
  const clampToContractStart = (date: Date): string => {
    return isBefore(date, contractStartDate)
      ? toDateOnly(contractStartDate, 'userTimezone')
      : toDateOnly(date, 'userTimezone');
  };

  if (!hasExplicitOfferingStartDate) {
    if (!isAmendment) {
      return toDateOnly(contractStartDate, 'userTimezone');
    }
    const relevantDate = getRelevantDayInMonth(anchorDay, new Date());
    return clampToContractStart(relevantDate);
  }

  if (isSameMonth(offeringStartDate, contractStartDate)) {
    return toDateOnly(contractStartDate, 'userTimezone');
  }

  const relevantDate = getRelevantDayInMonth(anchorDay, offeringStartDate);
  return clampToContractStart(relevantDate);
};

export const getRelevantOfferingStartDateForUsageOrMinCommit = (
  isAmendment: boolean,
  quote: IQuoteRespSchema,
  quoteOfferingStartDate?: string | null,
  isMinCommit = false,
) => {
  const contractStartDate = parseISO(quote.contractStartDate);
  const contractEndDate = parseISO(quote.contractEndDate);
  const hasExplicitOfferingStartDate = !!quoteOfferingStartDate;

  if (hasExplicitOfferingStartDate) {
    if (!isMinCommit) {
      return toDateOnly(quoteOfferingStartDate, 'userTimezone');
    }
    const relevantDate = getRelevantDayInMonth(
      getDate(contractStartDate),
      parseISO(quoteOfferingStartDate),
    );
    return toDateOnly(format(relevantDate, 'yyyy-MM-dd'), 'userTimezone');
  }

  if (!isAmendment) {
    return toDateOnly(contractStartDate, 'userTimezone');
  }

  const amendmentDate = quote.contractAmendmentDate
    ? parseISO(quote.contractAmendmentDate)
    : null;
  const targetDate = getNextOccurrenceSameDayOfMonth(
    getDate(amendmentDate || contractStartDate),
  );

  if (amendmentDate && isBefore(targetDate, amendmentDate)) {
    return toDateOnly(amendmentDate, 'userTimezone');
  }

  if (isBefore(contractEndDate, targetDate)) {
    return toDateOnly(contractStartDate, 'userTimezone');
  }

  return toDateOnly(targetDate, 'userTimezone');
};

export const getExpirationColors = (expirationDate: string) => {
  const daysRemaining = differenceInCalendarDays(
    new Date(expirationDate),
    new Date(),
  );

  if (daysRemaining <= 3) {
    return 'tRed.base';
  }
  if (daysRemaining <= 7) {
    return 'tOrange.tangerine';
  }
  return 'tRoyalBlue.base';
};

/**
 * Helper function to check if a product is non-prorated advance type
 * @param product - The product to check
 * @returns true if the product is non-prorated and advance type, false otherwise
 */
export const isNonProratedAdvanceProduct = (
  product:
    | {
        prorationPolicy?: ProrationPolicyEnum | null;
        productType?: ProductTypeEnum | null;
      }
    | null
    | undefined,
): boolean => {
  return (
    product?.prorationPolicy === ProrationPolicyEnum.NON_PRORATED &&
    product.productType === ProductTypeEnum.ADVANCE
  );
};

/**
 * Helper function to check if a product is non prorated
 * @param offering - The offering containing products
 * @param productId - The product ID to check
 * @returns true if the product is non prorated, false otherwise
 */
export const isProductNonProrated = (
  offering: Maybe<IOfferingRes>,
  productId: string,
): boolean => {
  const product = offering?.products?.find((prod) => prod.id === productId);
  return isNonProratedAdvanceProduct(product);
};

export const formatQuantityRangeTooltip = ({
  min,
  max,
}: {
  min: number;
  max: number | null;
}): string => {
  if (min > 1 && max === null) {
    return `Enter a quantity of ${formatInteger(min)} or more`;
  }
  if (min > 1 && max !== null) {
    return `Enter a quantity between ${formatInteger(min)} and ${formatInteger(
      max,
    )}`;
  }
  if (min <= 1 && max !== null) {
    return `Enter a quantity up to ${formatInteger(max)}`;
  }
  return '';
};
